import React, { useEffect, useRef, useState } from 'react';
import { productoMvp, nombreProducto, primaCelda, noAplicaEdad, ROTULO } from '../engine/perfil';
import { llano } from '../engine/llano';
import { PlanPicker } from './PlanPicker';
import catalogo from '../datos/catalogo.json';
import fichas from '../datos/fichas-productos.json';
import { tienePlanes } from '../engine/planes';
import { opcionesPlan } from '../engine/matriz';
import { fmtPesos } from '../engine/perfil';
import { perfilAutos, ajustarCobertura } from '../engine/matrizAutos';
import polizas from '../datos/polizas-comparador.json';

const FILAS = [
  ['principal', 'Cobertura principal'],
  ['valor', 'Valor asegurado'],
  ['condicion', 'Deducible o condición clave'],
  ['prima', 'Prima y periodicidad'],
  ['edad', 'Edad de ingreso'],
  ['no_cubre', 'Qué no cubre'],
];
// Autos con tuteo (caso A): solo filas que cambian entre opciones + taller del concesionario
const FILAS_AUTOS = [
  ['otros', 'Daños que le causes a otros'],
  ['pones', 'Lo que pones tú si chocas'],
  ['taller', 'Te reparan en el taller del concesionario'],
  ['prima', 'Prima'],
];
// Autos (sesión #5, Carlos): coberturas en la columna izquierda; en cada aseguradora solo valor y deducible,
// ya ajustados al perfil de riesgo (tope de RC y deducibles de la matriz)
const COB_AUTOS = [
  ['responsabilidad civil', 'Responsabilidad civil extracontractual'],
  ['pérdida total por daños', 'Pérdida total por daños'],
  ['pérdida parcial por daños', 'Pérdida parcial por daños'],
  ['pérdida total por hurto', 'Pérdida total por hurto'],
  ['pérdida parcial por hurto', 'Pérdida parcial por hurto'],
  ['terremoto', 'Terremoto y eventos de la naturaleza'],
  ['accidentes personales', 'Accidentes personales del conductor'],
  ['vehículo de reemplazo', 'Vehículo de reemplazo'],
  ['asistencia jurídica', 'Asistencia jurídica'],
  ['asistencia en viaje', 'Asistencia en viaje y grúa'],
];
// Valores tomados de las pólizas reales de referencia (datos/polizas-comparador.json, sin datos personales)
const POLIZA_DE = { 'eq-auto': 'equidad', 'sura-auto': 'sura', 'allianz-auto': 'allianz', 'axa-auto': null };
const VALOR_DEL_CARRO = /^(valor comercial|\$[\d.,]+ M)/i;
const coberturaAuto = (p, clave, pa) => {
  const fila = polizas.filas.find((f) => f.cobertura.toLowerCase().includes(clave));
  const v = fila && POLIZA_DE[p.id] ? fila.valores[POLIZA_DE[p.id]] : null;
  const desdePoliza = v ? {
    nombre: fila.cobertura,
    // El valor del carro de la póliza de referencia no es el de este cliente: se dice como regla
    valor: clave.startsWith('pérdida') && VALOR_DEL_CARRO.test(v.valor) ? 'Hasta el valor asegurado del carro' : v.valor,
    deducible: v.deducible,
  } : null;
  const c = desdePoliza || (p.coberturas || []).find((x) => x.nombre.toLowerCase().includes(clave));
  if (!c) return { t: 'No incluida' };
  const a = ajustarCobertura(c, pa);
  const ded = a.deducible && a.deducible !== '—' ? a.deducible : null;
  return { t: a.valor, ded, ajustado: a.ajustado };
};
// S14 · Cuántas opciones revisó el agente antes de mostrar estas (catálogo + fichas del ramo)
const CAT_RAMO = { autos: 'Automóvil', vida: 'Vida', salud: 'Salud' };
const FICHAS_VIDA = ['seguro-capital-en-vida', 'plan-vida-a-la-medida', 'seguro-integral-para-cancer', 'plan-vida-renta', 'plan-protege-tu-vida-suramericana'];
const revisadas = (ramo) => catalogo.productos.filter((p) => p.categoria === CAT_RAMO[ramo]).length
  + (ramo === 'vida' ? fichas.productos.filter((p) => FICHAS_VIDA.includes(p.id)).length : 0);
const ROTULO_TU = 'Prima de referencia publicada (oct-2026). La prima final te la confirma el asesor';
const SIN_PRECIO_TU = 'Te lo confirma el asesor';

// Productos con ficha parametrizada: valor y prima muestran SOLO lo que el perfil puede comprar (S2)
const textoPlanes = (p, k, answers, s) => {
  const op = opcionesPlan(p.id, answers);
  if (op.modo === 'caja') {
    if (k === 'valor') return { t: `De ${fmtPesos(op.min)} a ${fmtPesos(op.max)}` };
    return { t: s ? `Desde ${fmtPesos(op.primaDesde)} al mes; con ${s.label} se la confirma el asesor` : `Desde ${fmtPesos(op.primaDesde)} al mes` };
  }
  if (!op.planes.length) return { t: k === 'valor' ? 'Según su rango de edad' : 'Se la confirma el asesor' };
  if (k === 'valor') return { t: op.planes.map((x) => x.label).join(' · ') };
  if (s) return { t: `${s.label}: ${fmtPesos(s.prima)} al mes` };
  return { t: op.planes.map((x) => `${fmtPesos(x.prima)}`).join(' · ') + ' al mes' };
};

// El detalle tampoco puede mostrar lo que la matriz ocultó (S2)
const coberturasVisibles = (p, answers) => {
  const cs = p.coberturas || [];
  if (!tienePlanes(p.id)) return cs;
  const op = opcionesPlan(p.id, answers);
  if (op.modo === 'caja') {
    // El mismo factor de la severidad baja también el tope de accidente o invalidez (de $20 M a $200 M en la ficha)
    const f = op.max / op.maxFicha;
    return cs.map((c) => {
      if (/^Muerte por cualquier causa/.test(c.nombre)) return { ...c, valor: `De ${fmtPesos(op.min)} a ${fmtPesos(op.max)}` };
      if (/^Accidente o invalidez/.test(c.nombre)) return { ...c, valor: `De ${fmtPesos(20000000)} a ${fmtPesos(Math.max(20000000, Math.round(200000000 * f)))}` };
      return c;
    });
  }
  const labels = op.planes.map((x) => x.label);
  if (op.modo === 'lista') return cs.filter((c) => !/Plan \d+/.test(c.nombre) || labels.some((l) => c.nombre.endsWith(l)));
  return cs.map((c) => ({ ...c, valor: labels.join(', ') || 'Según su rango de edad' }));
};

const texto = (p, k, answers, tu, s) => {
  if ((k === 'valor' || k === 'prima') && tienePlanes(p.id) && noAplicaEdad(p, answers)) return { t: 'No aplica a su rango de edad' };
  if ((k === 'valor' || k === 'prima') && tienePlanes(p.id)) return textoPlanes(p, k, answers, s);
  if (k === 'prima') {
    const c = primaCelda(p, answers);
    return { t: tu && p.prima.en_linea ? SIN_PRECIO_TU : c.texto, ej: c.ejemplo };
  }
  if (k === 'edad') return { t: p.edad.texto };
  return { t: p.cmp[k] };
};

// Comparador: 3 columnas (o las que haya), primera columna fija en móvil. Nunca recomienda.
export const ComparadorMVP = ({ ids, extra = [], suyo, answers = {}, onElegir, onAsesor, locked, elegido, tu = false, titulo, sub }) => {
  const [verMas, setVerMas] = useState(false);
  // S3: plan o valor elegido por producto (solo productos con ficha parametrizada)
  const [sel, setSel] = useState({});
  const [detalle, setDetalle] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const sc = el && el.closest('.conv-scroll');
    if (sc) sc.scrollTo({ top: sc.scrollTop + el.getBoundingClientRect().top - sc.getBoundingClientRect().top - 8, behavior: 'smooth' });
  }, []);
  const prods = [...ids, ...(verMas ? extra : [])].map(productoMvp).filter(Boolean);
  const esAutos = prods[0] && prods[0].ramo === 'autos';
  const pa = esAutos ? perfilAutos(answers) : null;
  const base = esAutos ? [...COB_AUTOS.map(([k, l]) => [`cob:${k}`, l]), ['taller', 'Taller del concesionario'], ['prima', 'Prima']] : tu ? FILAS_AUTOS : FILAS;
  const celda = (p, k) => (k.startsWith('cob:') ? coberturaAuto(p, k.slice(4), pa) : texto(p, k, answers, tu, sel[p.id]));
  // Quita filas idénticas en todas las opciones (no ayudan a comparar), salvo la del taller
  const filas = base.filter(([k]) => esAutos || !tu || k === 'taller' || prods.length < 2 || new Set(prods.map((p) => texto(p, k, answers, tu, sel[p.id]).t)).size > 1);
  const L = (s) => llano(s, { usted: !tu });
  return (
    <section className="cmv" data-testid="comparador-mvp" ref={ref}>
      <div className="cmv-head">
        <span className="cmv-k" data-testid="cmv-titulo">{titulo || `Comparación de ${prods.length} opciones`}</span>
        {sub ? <span className="cmv-sub" data-testid="cmv-sub">{sub}</span> : null}
        {prods[0] && revisadas(prods[0].ramo) > prods.length ? (
          <span className="cmv-revise" data-testid="cmv-revise">
            {tu
              ? `Revisé ${revisadas(prods[0].ramo)} opciones; estas ${prods.length} son las que más se ajustan a lo que me contaste.`
              : `Revisé ${revisadas(prods[0].ramo)} opciones; estas ${prods.length} son las que más se ajustan a lo que me contó.`}
          </span>
        ) : null}
        {pa && pa.completo ? (
          <span className="cmv-revise" data-testid="cmv-perfil-autos">
            {`Ajustado a ${tu ? 'tus' : 'sus'} respuestas: responsabilidad civil ${pa.sus.rc_texto.toLowerCase()}, deducible por daños parciales ${pa.sus.ded_parcial}.`}
          </span>
        ) : null}
        <span className="cmv-note">
          {tu ? 'No te recomiendo una: te muestro las diferencias. Tú eliges.' : 'El asistente no recomienda: compara. Usted elige o habla con un asesor.'}
        </span>
      </div>
      <div className="cmv-scroll" data-testid="cmv-scroll">
        <table className="cmv-table">
          <thead>
            <tr>
              <th className="cmv-fija" scope="col">Opción</th>
              {prods.map((p) => (
                <th key={p.id} scope="col" data-testid={`cmv-col-${p.id}`}>
                  <span className="cmv-nombre">{nombreProducto(p)}</span>
                  {p.id === suyo ? <em className="cmv-tag">Su crédito</em> : null}
                  {noAplicaEdad(p, answers) ? <em className="cmv-tag amb">No aplica a su rango de edad</em> : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filas.map(([k, label]) => (
              <tr key={k}>
                <th className="cmv-fija" scope="row">{L(label)}</th>
                {prods.map((p) => {
                  const c = celda(p, k);
                  return (
                    <td key={p.id} data-testid={`cmv-${k}-${p.id}`} className={c.ajustado ? 'cmv-ajustado' : undefined}>
                      <span>{L(c.t)}</span>
                      {c.ded ? <small className="cmv-ded">{L('Deducible')}: {c.ded}</small> : null}
                      {c.ajustado ? <em className="cmv-tag amb">{tu ? 'Ajustado a tu perfil' : 'Ajustado a su perfil'}</em> : null}
                      {c.ej ? <b className="cmv-ejemplo" data-testid={`cmv-ejemplo-${p.id}`}>{c.ej}</b> : null}
                    </td>
                  );
                })}
              </tr>
            ))}
            {detalle ? (
              <tr className="cmv-detalle" data-testid="cmv-detalle">
                <th className="cmv-fija" scope="row">{L('Todas las coberturas')}</th>
                {prods.map((p) => (
                  <td key={p.id}>
                    <ul>
                      {coberturasVisibles(p, answers).map((c) => (
                        <li key={c.nombre}><b>{c.nombre}:</b> {c.valor}{c.deducible && c.deducible !== '—' ? ` · ${L('deducible')} ${c.deducible}` : ''}</li>
                      ))}
                    </ul>
                  </td>
                ))}
              </tr>
            ) : null}
            {prods.some((p) => tienePlanes(p.id)) ? (
              <tr className="cmv-planes">
                <th className="cmv-fija" scope="row">{L('Su plan')}</th>
                {prods.map((p) => (
                  <td key={p.id} data-testid={`cmv-plan-${p.id}`}>
                    {tienePlanes(p.id) && noAplicaEdad(p, answers) ? (
                      <span className="pp-aviso" data-testid={`pp-noaplica-${p.id}`}>No aplica a su rango de edad. Un asesor le muestra alternativas.</span>
                    ) : tienePlanes(p.id) ? (
                      <PlanPicker id={p.id} answers={answers} valor={sel[p.id]} locked={locked} onCambio={(v) => setSel((s) => ({ ...s, [p.id]: v }))} />
                    ) : null}
                  </td>
                ))}
              </tr>
            ) : null}
            <tr>
              <th className="cmv-fija" scope="row"> </th>
              {prods.map((p) => (
                <td key={p.id}>
                  <button
                    type="button"
                    className={`cmv-elegir ${elegido === p.id ? 'on' : ''}`}
                    disabled={locked || noAplicaEdad(p, answers) || (tienePlanes(p.id) && !sel[p.id])}
                    onClick={() => onElegir(p, sel[p.id] || null)}
                    data-testid={`cmv-elegir-${p.id}`}
                  >
                    {elegido === p.id ? 'Elegida' : 'Quiero esta opción'}
                  </button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <button type="button" className="cmv-vermas" onClick={() => setDetalle((d) => !d)} data-testid="cmv-ver-detalle">
        {detalle ? 'Ocultar el detalle de coberturas' : 'Ver todas las coberturas'}
      </button>
      {extra.length && !verMas ? (
        <button type="button" className="cmv-vermas" onClick={() => setVerMas(true)} data-testid="cmv-ver-mas">
          Ver más opciones ({extra.length})
        </button>
      ) : null}
      <p className="cmv-rotulo" data-testid="cmv-rotulo">{tu ? ROTULO_TU : ROTULO}.{esAutos ? ` ${polizas.rotulo}` : ''}</p>
      <button type="button" className="cmv-asesor" disabled={locked} onClick={onAsesor} data-testid="cmv-asesor">
        Prefiero hablar con un asesor
      </button>
    </section>
  );
};
